import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { useCart, usePurchaseFlowConfig, usePurchaseFlowOrderMeta } from 'components/pages/PurchaseFlow/_main/contexts'

import { BillingMethodInstructionCard } from '../BillingMethodInstructionCard'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import { EmailForwardning } from 'components/common/EmailForwardning'
import { Flow } from 'constants/misc'
import { InputWrap } from 'components/common/InputWrap'
import { InstructionType } from 'constants/purchaseFlow'
import { InvoicingAddressSection } from '../InvoicingAddressSection'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { Stack, Typography } from '@mui/material'
import { StepWrapper } from 'components/pages/PurchaseFlow/common/StepWrapper'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Whether to disable selecting/deselecting */
  disabledSelection?: boolean
  /** Whether to disable filling in the option fields */
  disabledFields?: boolean
}

/**
 * Shows Billing page in Purchase Flow.
 * 
 * @example
 * <Billing />
 */
export const Billing: React.FC<Props> = ({
  disabledSelection = false,
  disabledFields = false,
}) => {
  const { t } = useTranslation(['purchase_flow', 'purchase_flow_revamp', 'product'])
  const { roles } = useAuth0()

  const {
    getInstructionCatalogue,
    availablePaymentMethods,
    externalReportingInstruction,
  } = usePurchaseFlowConfig()

  const {
    reference,
    maxReferenceCharacters,
    setReference,
    propertyOwner,
    setPropertyOwner,
    externalReportingField,
    setExternalReportingField,
    ccEmails,
    setCcEmails
  } = usePurchaseFlowOrderMeta()

  const { total } = useCart()

  return (
    <StepWrapper
      title={t('billing_step.section_title')}
      subtitle={t('billing_step.section_description')}
    >
      {/* W2.3 (BX-F07): loading copy above the cylon spinner. Without it the
          user sees only a spinner in an otherwise blank form area — misleading
          because the cart sidebar already shows products and totals. */}
      {getInstructionCatalogue.isPending &&
        <Typography variant="text-sm" color="text.secondary" textAlign="center">
          {t('purchase_flow_revamp:billing.loading')}
        </Typography>
      }

      {/* W2.2 (BX-F08): onPurge enables the retry CTA in the error card when
          the instruction catalogue network request fails. Without it the user
          sees an error state with no way to recover without a full page reload. */}
      <DynamicQueryContent query={getInstructionCatalogue} onPurge={() => getInstructionCatalogue.reset()}>
        <Stack gap={2}>

          {/* Billing */}
          <SectionedBorderBox title={t('billing_step.order_information')}>
            <Stack gap={2}>

              <InputWrap label={t('billing_step.reference_field_label')} fieldNote={t('billing_step.reference_field_note')}>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value.replace(/(\r\n|\n|\r)/gm, '').slice(0, maxReferenceCharacters))} />
              </InputWrap>

              <InputWrap label={t('billing_step.property_owner_field_label')} fieldNote={t('billing_step.property_owner_field_note')}>
                <textarea value={propertyOwner} onChange={(e) => setPropertyOwner(e.target.value)}></textarea>
              </InputWrap>

              {!!externalReportingInstruction &&
                <InputWrap label={t(`product:p_${externalReportingInstruction.id}`)}>
                  <textarea
                    value={externalReportingField}
                    onChange={(e) => setExternalReportingField(e.target.value)}
                  ></textarea>
                </InputWrap>
              }

            </Stack>
          </SectionedBorderBox>

          {/* PAYMENT METHODS */}
          {total.totalAfterDiscount.gt(0) &&
            <SectionedBorderBox
              title={t('common.payment_method')}
            >
              <Stack gap={1}>
                {Object.values(availablePaymentMethods).map((paymentMethod, _, paymentMethods) => (
                  <BillingMethodInstructionCard
                    key={paymentMethod.id}
                    showSaveOption={roles.isClient && paymentMethods.length > 0}
                    isCardDisabled={disabledFields}
                    isSelectionDisabled={disabledSelection}
                    instructionOption={paymentMethod}
                    instructionType={InstructionType.BILLING_PRIMARY}
                  />
                ))}
              </Stack>
            </SectionedBorderBox>
          }

          {/* INVOICING ADDRESSES SECTION */}
          <InvoicingAddressSection />

          {/* Email forwarding */}
          <SectionedBorderBox
            title={t('billing_step.email_forwarding.title')}
            description={t('billing_step.email_forwarding.description')}
          >
            <EmailForwardning
              ccEmails={Array.from(ccEmails)}
              onChange={(emails) => {
                logAnalyticsEvent(AnalyticsEvent.CC_EMAIL_ADDED, { Flow: Flow.PURCHASE_FLOW })
                setCcEmails(new Set([...emails]))
              }}
              onDelete={(email) => {
                logAnalyticsEvent(AnalyticsEvent.CC_EMAIL_REMOVED, { Flow: Flow.PURCHASE_FLOW })
                setCcEmails((prev) => {
                  prev.delete(email)
                  return new Set(prev)
                })
              }}
            />
          </SectionedBorderBox>

        </Stack>
      </DynamicQueryContent>
    </StepWrapper>
  )
}
